/**
 * Widget Style Types
 * Defines the template system for embeddable widget customization
 */

/** Available widget template identifiers */
export type WidgetTemplateId =
  | 'classic'        // Current floating button + popup
  | 'compact'        // Smaller, minimal
  | 'immersive'      // Full LiveAgentPage in iframe
  | 'pill-button'    // Horizontal pill with text
  | 'side-tab'       // Tab on screen edge
  | 'banner'         // Full-width bottom bar
  | 'glassmorphism'  // Frosted glass effect
  | 'dark-mode'      // Dark theme variant
  | 'magic-v2';      // v2 widget — soft aura, sparkles, live-agent popup chrome.
                     // Loads /widget-v2.js (separate script), not /widget.js.

/** Button shape variants */
export type ButtonShape = 'circle' | 'rounded-square' | 'pill' | 'square';

/** Widget popup size presets */
export type WidgetSize = 'small' | 'medium' | 'large' | 'xl';

/** Animation style for popup appearance */
export type PopupAnimation = 'fade' | 'slide' | 'scale' | 'none';

/** Theme style options */
export type WidgetTheme = 'light' | 'dark' | 'auto';

/** Template category for grouping in the catalog */
export type TemplateCategory = 'core' | 'button-variants' | 'themes';

/** Complete widget style configuration */
export interface WidgetStyleConfig {
  templateId: WidgetTemplateId;
  button: {
    shape: ButtonShape;
    size: 'small' | 'medium' | 'large';
    showOnlineIndicator: boolean;
    showLabel: boolean;
    labelText?: string;
  };
  popup: {
    size: WidgetSize;
    borderRadius: number;
    animation: PopupAnimation;
    shadowIntensity: 'none' | 'light' | 'medium' | 'heavy';
  };
  theme: {
    style: WidgetTheme;
    glassEffect: boolean;
    accentOnHeader: boolean;
  };
}

/** Widget template definition */
export interface WidgetTemplate {
  id: WidgetTemplateId;
  name: string;
  description: string;
  category: TemplateCategory;
  config: WidgetStyleConfig;
  recommended?: boolean;
}

/** Size preset dimensions mapping */
export const WIDGET_SIZE_PRESETS: Record<WidgetSize, { width: number; height: number }> = {
  small: { width: 320, height: 480 },
  medium: { width: 380, height: 580 },
  large: { width: 440, height: 600 },
  xl: { width: 900, height: 700 },
};

/** Button size to pixel mapping */
export const BUTTON_SIZE_MAP: Record<'small' | 'medium' | 'large', number> = {
  small: 48,
  medium: 64,
  large: 80,
};

/** Shadow intensity CSS values */
export const SHADOW_PRESETS: Record<'none' | 'light' | 'medium' | 'heavy', string> = {
  none: 'none',
  light: '0 4px 12px rgba(0,0,0,0.08)',
  medium: '0 10px 40px rgba(0,0,0,0.15)',
  heavy: '0 20px 60px rgba(0,0,0,0.25)',
};

/** Button shape to border-radius mapping */
export const BUTTON_SHAPE_RADIUS: Record<ButtonShape, string> = {
  circle: '50%',
  'rounded-square': '16px',
  pill: '9999px',
  square: '8px',
};
